import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  LinearProgress,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

/**
 * Preview component for the eligibility refresh progress indicator
 * Simulates different states for testing the UI
 */
interface EligibilityRefreshProgressPreviewProps {
  open: boolean;
  onClose: () => void;
}

const EligibilityRefreshProgressPreview: React.FC<EligibilityRefreshProgressPreviewProps> = ({ open, onClose }) => {
  const [currentState, setCurrentState] = useState<'running' | 'completed' | 'failed'>('running');
  const [progress, setProgress] = useState(0);

  // Simulate progress when running
  useEffect(() => {
    if (open && currentState === 'running') {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            setCurrentState('completed');
            return 100;
          }
          return prev + Math.random() * 15; // Random increment
        });
      }, 500);

      return () => clearInterval(interval);
    }
  }, [open, currentState]);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setCurrentState('running');
      setProgress(0);
    }
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const getSeverity = () => {
    switch (currentState) {
      case 'failed': return 'error';
      case 'completed': return 'success';
      default: return 'info';
    }
  };

  const getMessage = () => {
    switch (currentState) {
      case 'failed':
        return 'Eligibility calculation failed. Please try again.';
      case 'completed':
        return 'Eligibility calculation completed!';
      default:
        return `Calculating eligibility... ${Math.round(progress)}%`;
    }
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ mb: 2 }}
    >
      <Alert
        severity={getSeverity()}
        onClose={currentState === 'running' ? handleClose : undefined}
        sx={{ minWidth: 300 }}
        action={
          currentState === 'running' ? (
            <IconButton
              size="small"
              aria-label="cancel"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" sx={{ mb: currentState === 'running' ? 1 : 0 }}>
            {getMessage()}
          </Typography>
          
          {currentState === 'running' && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {Math.round(progress)}% complete
              </Typography>
            </Box>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default EligibilityRefreshProgressPreview;
